import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, Eye, CheckCircle } from 'lucide-react';
import { getAccessToken } from '../services/auth';

// BC Product Setup - turns a BigCommerce product on the Unmapped Products/Variants report into
// the CPX rows fulfillment needs (products, prodcategories, ProductPacking, ProductsMappingCPXToBC).
// The SP behind it is usp_Setup_BCProductMapping; the recipe it automates is
// KB references/bc/reference_adding_bc_product_to_admin.md. Admin role only (server-enforced).

const FN_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8888/.netlify/functions/bc-product-setup'
  : '/.netlify/functions/bc-product-setup';

async function callFn(payload) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(FN_URL, { method: 'POST', headers, body: JSON.stringify(payload) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

// Pack count from a SKU or option label: "LIVE-LOB-1LB-8PK" -> 8, "100 count" -> 100.
function packCount(text) {
  const m = String(text || '').match(/(\d+)\s*-?\s*(PK|PACK|CT|COUNT)\b/i);
  return m ? Number(m[1]) : null;
}

const round2 = (n) => Math.round(n * 100) / 100;
const th = { textAlign: 'left', padding: '6px 8px', fontSize: 12, color: 'var(--gray-500)', borderBottom: '1px solid var(--gray-200)', whiteSpace: 'nowrap' };
const td = { padding: '4px 8px', fontSize: 13, borderBottom: '1px solid var(--gray-100)', verticalAlign: 'top' };
const inp = { padding: '4px 6px', fontSize: 13 };

export default function BCProductSetup() {
  // ?page=bc-product-setup&bcProductId=251 - the link in the Unmapped Products/Variants email.
  const [bcProductId, setBcProductId] = useState(() => (new URLSearchParams(window.location.search).get('bcProductId') || '').replace(/\D/g, ''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);         // { product, variants, categories }
  const [groupBy, setGroupBy] = useState('');      // option name, or '' = each variant alone
  const [mainCategory, setMainCategory] = useState('');
  const [groups, setGroups] = useState({});        // groupKey -> { master, sku, units, desc, categories: [] }
  const [rows, setRows] = useState({});            // variantId -> { include, name, price, weight, mult, factor, qty }
  const [preview, setPreview] = useState(null);    // { key, rows }
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const optionNames = useMemo(() => {
    const names = new Set();
    (data?.variants || []).forEach(v => v.options.forEach(o => names.add(o.name)));
    return [...names];
  }, [data]);

  const groupKeyOf = (v, by = groupBy) => {
    if (!by) return `v${v.id}`;
    const o = v.options.find(x => x.name === by);
    return o ? o.label : '(none)';
  };

  // Defaults for every group and row, recomputed whenever the grouping changes.
  const applyDefaults = (d, by) => {
    const g = {};
    d.variants.forEach(v => {
      const key = groupKeyOf(v, by);
      if (!g[key]) g[key] = { master: v.id, sku: '', units: '', desc: `${d.product.name} - ${by ? key : v.sku}`, categories: [] };
    });
    const r = {};
    d.variants.forEach(v => {
      const master = d.variants.find(x => x.id === g[groupKeyOf(v, by)].master);
      const n = packCount(v.sku) || packCount(v.options.map(o => o.label).join(' '));
      const mn = packCount(master.sku) || packCount(master.options.map(o => o.label).join(' '));
      const mult = n && mn ? Math.max(1, Math.round(n / mn)) : 1;
      const label = v.options.map(o => o.label).join(' ');
      r[v.id] = {
        include: true,
        name: label ? `${d.product.name} - ${label}` : d.product.name,
        price: v.price,
        weight: v.weight ? String(round2(v.weight / 16)) : '',
        mult, factor: mult, qty: mult,
      };
    });
    setGroups(g);
    setRows(r);
    setPreview(null);
  };

  const load = async () => {
    setLoading(true); setError(null); setData(null); setPreview(null); setDone(null);
    try {
      const d = await callFn({ action: 'load', bcProductId: Number(bcProductId) });
      setData(d);
      // Group by the option that is not the pack count, when there are two (Size x Quantity).
      const names = [...new Set(d.variants.flatMap(v => v.options.map(o => o.name)))];
      const by = names.length >= 2 ? (names.find(n => !/qty|quantity|pack|count/i.test(n)) || '') : '';
      setGroupBy(by);
      setMainCategory('');
      applyDefaults(d, by);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  // Arriving from the email link: load that product straight away, once.
  useEffect(() => {
    if (bcProductId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeGroupBy = (by) => { setGroupBy(by); if (data) applyDefaults(data, by); };
  const setGroup = (key, patch) => { setGroups(prev => ({ ...prev, [key]: { ...prev[key], ...patch } })); setPreview(null); };
  const setRow = (id, patch) => { setRows(prev => ({ ...prev, [id]: { ...prev[id], ...patch } })); setPreview(null); };

  const buildRequest = () => {
    const variants = data.variants.filter(v => rows[v.id]?.include).map(v => {
      const key = groupKeyOf(v);
      const g = groups[key];
      const r = rows[v.id];
      return {
        bcVariantId: v.id,
        group: key,
        isMaster: g.master === v.id,
        name: r.name,
        price: Number(r.price),
        weight: r.weight,
        imageUrl: v.imageUrl || data.product.imageUrl || '',
        multiplier: Number(r.mult),
        categoryIds: [...new Set([Number(mainCategory), ...g.categories.map(Number)])].filter(Boolean),
        packing: [{ sku: g.sku, units: g.units, factor: Number(r.factor), qty: Number(r.qty), description: g.desc }],
      };
    });
    return { bcProductId: data.product.id, categoryId: Number(mainCategory), variants };
  };

  const run = async (dryRun) => {
    setBusy(true); setError(null);
    const request = buildRequest();
    const key = JSON.stringify(request);
    try {
      if (!dryRun && !window.confirm(`Create ${request.variants.length} products and mappings for BC ${request.bcProductId} in PRODUCTION?`)) {
        setBusy(false); return;
      }
      const res = await callFn({ action: 'setup', dryRun, request });
      if (dryRun) setPreview({ key, rows: res.rows });
      else { setDone(res.rows); setPreview(null); }
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const previewIsCurrent = preview && data && preview.key === JSON.stringify(buildRequest());
  const groupKeys = data ? [...new Set(data.variants.map(v => groupKeyOf(v)))] : [];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1500 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>BC Product Setup</h1>
      <p style={{ fontSize: 13, color: 'var(--gray-500)', margin: '4px 0 16px' }}>
        Set up a BigCommerce product from the Unmapped Products/Variants report so its orders reach fulfillment.
        Preview first; Create writes to production.
      </p>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input className="form-input" style={{ width: 200 }} placeholder="BC product id (e.g. 251)"
            value={bcProductId} onChange={e => setBcProductId(e.target.value.replace(/\D/g, ''))}
            onKeyDown={e => { if (e.key === "Enter" && bcProductId) load(); }} />
          <button className="btn btn-primary" onClick={load} disabled={!bcProductId || loading}>
            {loading ? <RefreshCw size={14} className="spin" /> : <Search size={14} />} Load
          </button>
          {data && (
            <span style={{ fontSize: 13, marginLeft: 8 }}>
              <b>{data.product.name}</b> &middot; SKU {data.product.sku} &middot; {data.variants.length} variants
              {!data.product.isVisible && <span style={{ color: '#b45309' }}> &middot; hidden on BC</span>}
            </span>
          )}
        </div>
      </div>

      {error && <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, background: '#fde8e8', color: '#c53030' }}>{error}</div>}

      {done && (
        <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, background: '#e6f9e6', color: '#1a7a1a' }}>
          <CheckCircle size={14} /> Created {done.length} products: catalog {done[0]?.catalogId}-{done[done.length - 1]?.catalogId},
          mappings {done[0]?.mappingId}-{done[done.length - 1]?.mappingId}. It should leave the Unmapped report on its next run.
        </div>
      )}

      {data && !done && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ padding: '12px 16px', display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
              <label>Group variants by{' '}
                <select className="form-input" style={inp} value={groupBy} onChange={e => changeGroupBy(e.target.value)}>
                  <option value="">Nothing (each variant is its own product)</option>
                  {optionNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <label>Main category{' '}
                <select className="form-input" style={inp} value={mainCategory} onChange={e => { setMainCategory(e.target.value); setPreview(null); }}>
                  <option value="">Pick...</option>
                  {data.categories.map(c => <option key={c.id} value={c.id}>{c.name} ({c.id})</option>)}
                </select>
              </label>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ padding: '12px 16px', overflowX: 'auto' }}>
              <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>Warehouse (WOS) item per group</h3>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr>
                  <th style={th}>Group</th><th style={th}>Master variant</th><th style={th}>WOS SKU</th>
                  <th style={th}>WOS unit</th><th style={th}>Packing list description</th><th style={th}>Extra categories</th>
                </tr></thead>
                <tbody>
                  {groupKeys.map(key => {
                    const g = groups[key]; if (!g) return null;
                    const members = data.variants.filter(v => groupKeyOf(v) === key);
                    return (
                      <tr key={key}>
                        <td style={td}>{key}</td>
                        <td style={td}>
                          <select className="form-input" style={inp} value={g.master} onChange={e => setGroup(key, { master: Number(e.target.value) })}>
                            {members.map(v => <option key={v.id} value={v.id}>{v.sku}</option>)}
                          </select>
                        </td>
                        <td style={td}><input className="form-input" style={{ ...inp, width: 90 }} placeholder="CP520" value={g.sku} onChange={e => setGroup(key, { sku: e.target.value.toUpperCase() })} /></td>
                        <td style={td}><input className="form-input" style={{ ...inp, width: 90 }} placeholder="2-Pack" value={g.units} onChange={e => setGroup(key, { units: e.target.value })} /></td>
                        <td style={td}><input className="form-input" style={{ ...inp, width: 300 }} value={g.desc} onChange={e => setGroup(key, { desc: e.target.value })} /></td>
                        <td style={td}>
                          {g.categories.map(c => (
                            <span key={c} style={{ display: 'inline-block', background: 'var(--gray-100)', borderRadius: 4, padding: '1px 6px', marginRight: 4, fontSize: 12 }}>
                              {(data.categories.find(x => String(x.id) === String(c)) || {}).name || c}{' '}
                              <button style={{ border: 0, background: 'none', cursor: 'pointer' }} onClick={() => setGroup(key, { categories: g.categories.filter(x => x !== c) })}>x</button>
                            </span>
                          ))}
                          <select className="form-input" style={inp} value="" onChange={e => { if (e.target.value) setGroup(key, { categories: [...new Set([...g.categories, e.target.value])] }); }}>
                            <option value="">+ add</option>
                            {data.categories.map(c => <option key={c.id} value={c.id}>{c.name} ({c.id})</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ padding: '12px 16px', overflowX: 'auto' }}>
              <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>Variants</h3>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr>
                  <th style={th}></th><th style={th}>Variant</th><th style={th}>BC SKU</th><th style={th}>CPX name</th>
                  <th style={th}>Price</th><th style={th}>BC cost</th><th style={th}>Weight (lb)</th>
                  <th style={th} title="masterlist multiplier vs the group's master">x Master</th>
                  <th style={th} title="WOS units consumed per item">WOS factor</th>
                  <th style={th} title="quantity printed on the packing list">Packing qty</th>
                </tr></thead>
                <tbody>
                  {data.variants.map(v => {
                    const r = rows[v.id]; if (!r) return null;
                    const isMaster = groups[groupKeyOf(v)]?.master === v.id;
                    const atOrBelowCost = v.costPrice > 0 ? Number(r.price) <= v.costPrice : false;
                    return (
                      <tr key={v.id} style={{ opacity: r.include ? 1 : 0.45 }}>
                        <td style={td}><input type="checkbox" checked={r.include} onChange={e => setRow(v.id, { include: e.target.checked })} /></td>
                        <td style={td}>{v.id}{isMaster ? <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--navy)', fontWeight: 600 }}>master</span> : null}</td>
                        <td style={td}>{v.sku}</td>
                        <td style={td}><input className="form-input" style={{ ...inp, width: 340 }} value={r.name} onChange={e => setRow(v.id, { name: e.target.value })} /></td>
                        <td style={td}><input className="form-input" style={{ ...inp, width: 80 }} value={r.price} onChange={e => setRow(v.id, { price: e.target.value })} /></td>
                        <td style={{ ...td, color: atOrBelowCost ? '#c53030' : 'var(--gray-500)' }}>{v.costPrice}</td>
                        <td style={td}><input className="form-input" style={{ ...inp, width: 60 }} value={r.weight} onChange={e => setRow(v.id, { weight: e.target.value })} /></td>
                        <td style={td}><input className="form-input" style={{ ...inp, width: 50 }} value={r.mult} onChange={e => setRow(v.id, { mult: e.target.value.replace(/\D/g, '') })} /></td>
                        <td style={td}><input className="form-input" style={{ ...inp, width: 60 }} value={r.factor} onChange={e => setRow(v.id, { factor: e.target.value })} /></td>
                        <td style={td}><input className="form-input" style={{ ...inp, width: 50 }} value={r.qty} onChange={e => setRow(v.id, { qty: e.target.value.replace(/\D/g, '') })} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '8px 0 0' }}>
                Price defaults to the BC price; a BC cost in red means the price is at or below cost.
                Weight is the BC weight (oz) / 16.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button className="btn btn-navy" onClick={() => run(true)} disabled={busy || !mainCategory}>
              {busy ? <RefreshCw size={14} className="spin" /> : <Eye size={14} />} Preview
            </button>
            <button className="btn btn-primary" onClick={() => run(false)} disabled={busy || !previewIsCurrent}
              title={previewIsCurrent ? '' : 'Run Preview after your last change first'}>
              Create in production
            </button>
          </div>

          {preview ? (
            <div className="card">
              <div className="card-body" style={{ padding: '12px 16px', overflowX: 'auto' }}>
                <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>
                  Preview (rolled back; real ids will be higher)
                  {previewIsCurrent ? null : <span style={{ color: '#b45309' }}> - out of date, preview again</span>}
                </h3>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead><tr>
                    <th style={th}>Variant</th><th style={th}>Catalog</th><th style={th}>Name</th><th style={th}>Price</th>
                    <th style={th}>Weight</th><th style={th}>masterlist</th><th style={th}>Order</th>
                    <th style={th}>Categories</th><th style={th}>Packing</th>
                  </tr></thead>
                  <tbody>
                    {preview.rows.map(r => (
                      <tr key={r.bcVariantId}>
                        <td style={td}>{r.bcVariantId}</td><td style={td}>{r.catalogId}</td><td style={td}>{r.name}</td>
                        <td style={td}>{r.price}</td><td style={td}>{r.weight}</td><td style={td}>{r.masterList}</td>
                        <td style={td}>{r.masterOrder}</td><td style={td}>{r.categories}</td><td style={td}>{r.packing}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
